import type { ParsedKeyframesMap } from "./types";
import { parseNumericAttribute } from "./attributeUtils";
import { getDirectChildElementsByTag } from "./xmlUtils";

export const UNSUPPORTED_PLUGIN_SURFACE_WARNING_PREFIX = "Unsupported MJCF plugin surface:";

export const parseKeyframeDetails = (xmlDoc: Document): ParsedKeyframesMap => {
  const keyframes: ParsedKeyframesMap = {};
  const keyframeElements = xmlDoc.querySelectorAll("keyframe > key");
  keyframeElements.forEach((key: Element) => {
    const name = key.getAttribute("name");
    if (!name) return;
    const qpos = parseNumericAttribute(key.getAttribute("qpos"));
    if (!qpos) return;
    const ctrl = parseNumericAttribute(key.getAttribute("ctrl"));
    const mpos = parseNumericAttribute(key.getAttribute("mpos"));
    const mquat = parseNumericAttribute(key.getAttribute("mquat"));
    const act = parseNumericAttribute(key.getAttribute("act"));
    keyframes[name] = {
      qpos,
      ...(ctrl ? { ctrl } : {}),
      ...(mpos ? { mpos } : {}),
      ...(mquat ? { mquat } : {}),
      ...(act ? { act } : {}),
    };
  });
  return keyframes;
};

export const parseKeyframes = (xmlDoc: Document): Record<string, number[]> => {
  const keyframes: Record<string, number[]> = {};
  if (xmlDoc.querySelectorAll("keyframe > key").length === 0) return keyframes;

  Object.entries(parseKeyframeDetails(xmlDoc)).forEach(([name, detail]) => {
    keyframes[name] = detail.qpos;
  });
  return keyframes;
};

export const parsePreservedMjcfSections = (
  xmlDoc: Document
): { custom?: string; extension?: string; lights?: string[] } => {
  const serialize = (node: Element | null): string | undefined => {
    if (!node) return undefined;
    return new XMLSerializer().serializeToString(node);
  };
  const custom = serialize(xmlDoc.querySelector("mujoco > custom"));
  const extension = serialize(xmlDoc.querySelector("mujoco > extension"));
  const lights: string[] = [];
  const seenLightNames = new Set<string>();
  const worldbodies = Array.from(xmlDoc.querySelectorAll("mujoco > worldbody"));
  worldbodies.forEach((worldbody) => {
    getDirectChildElementsByTag(worldbody, "light").forEach((lightElem) => {
      const lightName = lightElem.getAttribute("name")?.trim() || "";
      if (lightName) {
        if (seenLightNames.has(lightName)) return;
        seenLightNames.add(lightName);
      }
      const serialized = new XMLSerializer().serializeToString(lightElem);
      if (serialized && serialized.trim()) {
        lights.push(serialized.trim());
      }
    });
  });
  return {
    ...(custom ? { custom } : {}),
    ...(extension ? { extension } : {}),
    ...(lights.length > 0 ? { lights } : {}),
  };
};

export const parseReferencedFiles = (xmlDoc: Document): string[] => {
  const includeFiles = Array.from(xmlDoc.querySelectorAll("include")).map((i) =>
    i.getAttribute("file")?.trim()
  );
  const objFiles = Array.from(xmlDoc.querySelectorAll("mesh")).map((m) =>
    (m.getAttribute("file") || m.getAttribute("obj"))?.trim()
  );
  const texFiles = Array.from(xmlDoc.querySelectorAll("texture")).map((t) =>
    t.getAttribute("file")?.trim()
  );
  const hfieldFiles = Array.from(xmlDoc.querySelectorAll("hfield")).map((h) =>
    h.getAttribute("file")?.trim()
  );
  return [...includeFiles, ...objFiles, ...texFiles, ...hfieldFiles].filter((f): f is string =>
    Boolean(f)
  );
};

const getElementLabel = (element: Element): string => {
  const name = element.getAttribute("name")?.trim();
  if (name) return `"${name}"`;
  const parentName = element.parentElement?.getAttribute("name")?.trim();
  return parentName ? `"${parentName}"` : "unnamed";
};

const directPluginChildren = (parentTagName: string, xmlDoc: Document): Element[] =>
  Array.from(xmlDoc.querySelectorAll(parentTagName)).flatMap((parent) =>
    getDirectChildElementsByTag(parent, "plugin")
  );

const directSensorPluginElements = (xmlDoc: Document): Element[] =>
  Array.from(xmlDoc.querySelectorAll("sensor")).flatMap((sensorRoot) =>
    getDirectChildElementsByTag(sensorRoot, "plugin")
  );

export const detectUnsupportedPluginSurfaceWarnings = (xmlDoc: Document): string[] => {
  const surfaces = [
    { label: "mesh/plugin", elements: directPluginChildren("mesh", xmlDoc) },
    { label: "geom/plugin", elements: directPluginChildren("geom", xmlDoc) },
    { label: "composite/plugin", elements: directPluginChildren("composite", xmlDoc) },
    { label: "flexcomp/plugin", elements: directPluginChildren("flexcomp", xmlDoc) },
    { label: "sensor/plugin", elements: directSensorPluginElements(xmlDoc) },
  ].filter((surface) => surface.elements.length > 0);

  if (surfaces.length === 0) return [];

  const summary = surfaces
    .map(
      (surface) =>
        `${surface.elements.length} ${surface.label} element${
          surface.elements.length === 1 ? "" : "s"
        }`
    )
    .join(", ");
  const examples = surfaces
    .map((surface) => `${surface.label} ${getElementLabel(surface.elements[0])}`)
    .join("; ");

  return [
    `${UNSUPPORTED_PLUGIN_SURFACE_WARNING_PREFIX} ${summary} detected. Critter currently preserves actuator plugins and body-local plugins, but these plugin surfaces are not fully round-tripped yet and may be omitted or degraded on export. Examples: ${examples}.`,
  ];
};
